/**
 * @fileoverview Validates project agent model configurations against the
 * current list of available models. Detects agents (and composite step overrides)
 * that reference models which no longer exist.
 */

import {
  ALL_AVAILABLE_MODELS,
  FAL_IMAGE_MODELS,
  FAL_MULTI_REFERENCE_MODELS,
} from '@/ai/models';
import type { ProjectAgent } from '@/domains/agents/schemas/agent';
import { resolveDefaultModelConfig } from '@/domains/agents/presets/apply-preset';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InvalidModelEntry {
  /** The project agent that has an invalid model */
  agent: ProjectAgent;
  /** The provider that doesn't exist or whose model list doesn't contain the model */
  invalidProvider: string;
  /** The model name that is no longer available */
  invalidModelName: string;
  /**
   * If the invalid model is on a step override (composite agents), this is
   * the step agent ID. Undefined means the top-level model is invalid.
   */
  stepAgentId?: string;
  /** Whether this entry is for a FAL image model (as opposed to a text model) */
  isImageModel?: boolean;
  /** Suggested replacement provider from the agent's static definition (if still valid) */
  suggestedProvider?: string;
  /** Suggested replacement model from the agent's static definition (if still valid) */
  suggestedModelName?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Whether the given provider is the FAL image provider */
function isFalProvider(provider: string): boolean {
  return provider === 'fal-ai';
}

/**
 * Whether a given model name refers to a FAL-routed image model.
 *
 * Image models can be stored under any provider prefix — the model name is
 * self-describing and is what routes the request through FAL. For example,
 * Grok Imagine is stored with `modelProvider: 'xai'` but the actual request
 * goes to FAL's xAI backend. Detection must therefore look at the model name,
 * not the provider.
 */
function isImageModelName(modelName: string): boolean {
  return (
    FAL_IMAGE_MODELS.some((m) => m.value === modelName) ||
    FAL_MULTI_REFERENCE_MODELS.some((m) => m.value === modelName)
  );
}

/**
 * Whether a given agent/step model config should be skipped during validation.
 *
 * Image models are excluded from validation. A config is considered an image
 * model if either:
 *   1. Its provider is `fal-ai` (historical behavior), OR
 *   2. Its model name is registered in the FAL image-model registries
 *      regardless of provider prefix — this covers Grok Imagine, which is
 *      stored with `modelProvider: 'xai'` but routed through FAL.
 */
function isImageModelConfig(provider: string, modelName: string): boolean {
  return isFalProvider(provider) || isImageModelName(modelName);
}

/**
 * Look up the default preset model config for an agent and return it as a
 * suggestion — but only if that default model is still available.
 *
 * For step overrides, `stepAgentId` identifies the step's own agent.
 */
function getSuggestedDefault(
  agentId: string,
  stepAgentId?: string,
): { provider: string; modelName: string } | undefined {
  const defId = stepAgentId ?? agentId;
  const { modelProvider, modelName } = resolveDefaultModelConfig(defId);
  if (isModelAvailable(modelProvider, modelName)) {
    return { provider: modelProvider, modelName };
  }
  return undefined;
}

/**
 * Check whether a provider + model name pair exists in any of the model
 * registries. FAL image registries are checked first (by model name alone)
 * because image models can be stored under arbitrary provider prefixes —
 * e.g. `xai/grok-imagine-image` is routed through FAL regardless of its
 * `modelProvider` being `'xai'`.
 */
function isModelAvailable(provider: string, modelName: string): boolean {
  if (isFalProvider(provider)) {
    return FAL_IMAGE_MODELS.some((m) => m.value === modelName);
  }
  if (isImageModelName(modelName)) return true;
  const providerModels =
    ALL_AVAILABLE_MODELS[provider as keyof typeof ALL_AVAILABLE_MODELS];
  if (!providerModels) return false;
  return providerModels.some((m) => m.value === modelName);
}

// ---------------------------------------------------------------------------
// Main validation
// ---------------------------------------------------------------------------

/**
 * Validate all project agents and return a list of entries whose configured
 * model no longer exists in `ALL_AVAILABLE_MODELS`.
 *
 * When `agentDefinitions` is provided, each invalid entry will include a
 * suggested replacement model from the agent's static definition (if that
 * default is still available).
 *
 * Checks:
 * 1. The agent's top-level `modelProvider` / `modelName`
 * 2. Each entry in `stepOverrides` (composite agents)
 */
export function validateAgentModels(
  agents: ProjectAgent[],
): InvalidModelEntry[] {
  const invalid: InvalidModelEntry[] = [];

  for (const agent of agents) {
    // 1. Top-level model check (skip image models — they're excluded from validation)
    if (
      !isImageModelConfig(agent.modelProvider, agent.modelName) &&
      !isModelAvailable(agent.modelProvider, agent.modelName)
    ) {
      const suggested = getSuggestedDefault(agent.id);
      invalid.push({
        agent,
        invalidProvider: agent.modelProvider,
        invalidModelName: agent.modelName,
        isImageModel: false,
        suggestedProvider: suggested?.provider,
        suggestedModelName: suggested?.modelName,
      });
    }

    // 2. Step-override checks (composite agents, skip image models)
    if (agent.stepOverrides) {
      for (const [stepAgentId, override] of Object.entries(agent.stepOverrides)) {
        if (
          !isImageModelConfig(override.modelProvider, override.modelName) &&
          !isModelAvailable(override.modelProvider, override.modelName)
        ) {
          const suggested = getSuggestedDefault(agent.id, stepAgentId);
          invalid.push({
            agent,
            invalidProvider: override.modelProvider,
            invalidModelName: override.modelName,
            stepAgentId,
            isImageModel: false,
            suggestedProvider: suggested?.provider,
            suggestedModelName: suggested?.modelName,
          });
        }
      }
    }
  }

  return invalid;
}
